import { defineSemanticTokens } from '@chakra-ui/react';

export const colors = defineSemanticTokens.colors({
  bg: {
    // ── Solid surfaces (brand HSL scale) ──
    DEFAULT: {
      value: '{colors.brand.900}',
    },
    surface: {
      value: '{colors.brand.800}',
    },
    card: {
      value: '{colors.brand.700}',
    },
    emphasized: {
      value: '{colors.brand.600}',
    },
    /**
     * Карточка под курсором. Одна ступень над `bg.card` (12,5% → 16,5%, это 1,14:1), а не
     * прыжок до `bg.emphasized` (20%, 1,26:1): всё остальное в продукте поднимается под
     * курсором ровно на одну ступень, и карточка, прыгающая на полторы, кричала громче
     * собственной кнопки. `bg.emphasized` остаётся тегам и подсветке, где ему и место.
     *
     * Сплошной цвет, а не полупрозрачный: у карточки-объекта заливка сплошная, и полупрозрачную
     * ступень поверх неё одной заливкой не выразить. Значение подобрано так, чтобы шаг совпал
     * с шагом `bg.muted` над страницей.
     */
    raised: {
      value: 'hsl(228, 11.6%, 16.5%)',
    },
    /**
     * «Выбрано»: активный пункт бокового меню и навигации настроек, выбранная плита. Одно
     * значение для навигации, плит, Pills и выбранных тегов. Мягкая подсветка purple.200
     * согласована в превью: заливка 12%, наведение 20%, обводка 30%.
     *
     * Акцент, а не нейтральный подъём: наведение в системе серое (`bg.muted`), и выбранное
     * обязано отличаться от него не яркостью, а цветом, иначе курсор и выбор сливаются.
     */
    selected: {
      value: '{colors.purple.200/12}',
    },
    selectedHover: {
      value: '{colors.purple.200/20}',
    },
    inverted: {
      value: '{colors.white}',
    },
    // ── Transparent overlays (whiteAlpha) ──
    /**
     * Самая тихая поверхность: плашка с примечанием, секция фильтров, свёрнутая шторка
     * статистики. На ступень ниже `bg.muted`: её берут, когда блок надо отделить от страницы,
     * но не поднимать над ней.
     *
     * Заведена потому, что одиннадцать мест брали `whiteAlpha.50` напрямую. Значение верное, но
     * белая вуаль остаётся белой при любой теме, а роль знает обе.
     */
    subtle: {
      value: '{colors.whiteAlpha.50}',
    },
    muted: {
      value: '{colors.whiteAlpha.100}',
    },
    // Заливка пилюли. Своё значение, а не ступень whiteAlpha: на тёмном шаги шкалы прыгают мимо
    // нужного места. Подбиралось сверху вниз, с замером на каждом шаге: 0.16 — 1.60:1 к странице,
    // кричит громче собственного текста; 0.12 — 1.39:1, всё ещё ярче соседних фактов.
    //
    // 0.09 держалось долго, но у него был не замеченный замером сосед: заливка игрового тега
    // (`bg.emphasized`). На карточке ленты они давали #313237 против #2d2f39 — 0.27 пункта
    // яркости, то есть один и тот же серый. Тег и чип отвечают на разные вопросы («по этому
    // ищут» против «вот факт о человеке») и обязаны различаться материалом, а не только тем,
    // что написано внутри.
    //
    // 0.05 — это 1.15:1 к карточке и 1.12:1 к странице: форма пилюли читается, но она заметно
    // темнее тега (1.10:1 между их заливками). Ниже не опускать: на 0.04 пилюля пропадает и
    // остаются висеть значок с подписью. Темнее фона тоже нельзя — поверхности здесь почти
    // чёрные, и затемнённая пилюля на странице даёт 1.03:1, то есть исчезает вовсе.
    //
    // Полупрозрачность обязательна: сплошной токен рано или поздно совпадёт с поверхностью,
    // на которую пилюля легла.
    chip: {
      value: 'rgba(255, 255, 255, 0.05)',
    },
    // Заливка скелетона. Не bg.emphasized (brand.600): заглушка на нём получается СВЕТЛЕЕ
    // карточки, которую заменяет (bg.card = brand.700), и ярче страницы (brand.900) — пустая
    // лента выглядела подсвеченной, хотя ждать нечего. Полупрозрачный белый на полтона выше
    // страницы: форма карточки читается, но в глаза не бьёт и работает на любой поверхности.
    skeleton: {
      DEFAULT: {
        value: { _light: '{colors.blackAlpha.100}', _dark: 'rgba(255, 255, 255, 0.05)' },
      },
      /*
        Блик, который бежит по заглушке (вариант `shine`). Сток брал `bg.muted` (0.06) - на
        полтона выше самой заливки (0.05), и движение не видел никто: лента на холодном старте
        читалась как пустое место. 0.12 на карточке - 1.44:1 против 1.17:1 у заливки, на шапке
        карточки (`bg.emphasized`) - 1.46:1 против 1.15:1: блик виден, но не светлее текста.
        Полупрозрачный по той же причине, что и заливка: заглушка лежит и на странице, и на
        карточке, и на её шапке. В светлой теме наоборот - блик светлее заливки.
      */
      shine: {
        value: { _light: '{colors.blackAlpha.50}', _dark: 'rgba(255, 255, 255, 0.12)' },
      },
    },
    overlay: {
      DEFAULT: {
        value: 'hsla(228, 11.6%, 20%, 0.5)',
      },
      // Та же подложка под курсором: кнопки поверх обложки заявки светлеют, не теряя прозрачности.
      hover: {
        value: 'hsla(228, 11.6%, 20%, 0.7)',
      },
    },
    /*
      Стекло: полупрозрачная панель поверх содержимого, всегда вместе с размытием фона.

      Четыре места писали этот материал руками и разошлись: шапка и нижняя панель брали цвет
      страницы на 78%, поповер эмодзи - 12% яркости на 97%, панель действий над сообщением - 14%
      на 92%. Разницу между 92% и 97% не видит никто, а починить прозрачность разом было нельзя.

      `glass` - слой уровня страницы: шапка, нижняя панель. `glass.raised` - то, что всплывает
      над содержимым: поповер, панель действий; она на ступень светлее, как и всё поднятое.
    */
    glass: {
      DEFAULT: {
        value: 'hsla(228, 11.6%, 8%, 0.78)',
      },
      raised: {
        value: 'hsla(228, 11.6%, 13%, 0.95)',
      },
    },
    error: {
      value: {
        _light: '{colors.red.50}',
        _dark: '{colors.red.950}',
      },
    },
    warning: {
      value: {
        _light: '{colors.orange.50}',
        _dark: '{colors.orange.950}',
      },
    },
    success: {
      value: {
        _light: '{colors.green.50}',
        _dark: '{colors.green.950}',
      },
    },
    info: {
      value: {
        _light: '{colors.blue.50}',
        _dark: '{colors.blue.950}',
      },
    },
  },
  fg: {
    // Foregrounds are opaque: text and SVG glyphs share these roles. Alpha in currentColor
    // compounds where separate SVG shapes overlap. The neutral scale matches the former
    // whiteAlpha scale composited over bg.raised. Muted is lifted to remain AA on emphasized
    // and nested chip surfaces; the text hierarchy remains distinct.
    DEFAULT: { value: '#eeeeef' },
    emphasized: { value: '#d3d4d5' },
    secondary: { value: '#b1b1b4' },
    muted: { value: '#999b9f' },
    // Decorative detail, not body text.
    subtle: { value: '#484a50' },
    // Standard neutral metadata glyph; deliberately below secondary text.
    icon: { value: 'hsl(220, 1.2%, 60%)' },
    inverted: {
      value: '{colors.black}',
    },
    error: {
      value: '{colors.red.400}',
    },
    warning: {
      value: '{colors.orange.200}',
    },
    success: {
      value: '{colors.green.400}',
    },
    info: {
      value: '{colors.blue.300}',
    },
    /**
     * Цвет всего, на что можно нажать: ссылки в тексте, «показать ещё», внешние ссылки.
     *
     * Фиолетовый, а не бирюзовый. Бирюзовый пришёл тремя разными задачами и фирменным никогда
     * не был; при этом он же стоял на времени собеседника и адресе почты, нажать на которые
     * нельзя, — цвет обещал нажимаемость и обещания не выполнял.
     *
     * Правило теперь одно и без исключений: покрашено — значит нажимается. То же самое, по
     * которому убирали фиолетовые пилюли, просто доведённое до текста. Отсюда и следствие:
     * имена авторов, подписи и подставленные значения цвета не получают, их выделяет
     * начертание.
     *
     * Ступень 100 даёт 6.00:1 на фоне страницы при пороге 4.5. На наведении светлее, а не
     * темнее: на тёмном фоне уводить ссылку вниз по шкале значит гасить её.
     */
    link: {
      value: '{colors.purple.100}',
    },
    'link.hover': {
      value: '{colors.purple.50}',
    },
  },
  border: {
    /**
     * Линия в один пиксель. Две роли, не больше:
     *
     * - `border` - линия, которая разделяет: строки списка, шапка карточки от её содержимого,
     *   низ панели. Её задача - показать, что это два разных куска.
     * - `subtle` - линия, которая очерчивает: край поля ввода, рамка неактивной плитки. Её
     *   задача - обозначить границу предмета, не притягивая взгляд.
     *
     * `muted` отсюда убран: он был точной копией `subtle` (оба whiteAlpha.100) и не
     * использовался ни разу. Два имени на один цвет - это гарантия, что через месяц половина
     * кода возьмёт одно, половина другое, и никто не вспомнит почему.
     */
    DEFAULT: {
      value: '{colors.whiteAlpha.200}',
    },
    subtle: {
      value: '{colors.whiteAlpha.100}',
    },
    selected: {
      value: '{colors.purple.200/30}',
    },
    inverted: {
      value: '{colors.gray.200}',
    },
    /*
      Линия состояния: рамка поля с ошибкой, край предупреждающего баннера.

      Эти четыре имени однажды уже убрали заодно с дублем `muted` (11.09.2026), а ссылки на них
      остались: `--error-color` в `input`, `textarea`, `combobox` и `select`, жёлтая рамка
      баннера предпросмотра. Несуществующий токен Chakra не подставляет и не ругается - в CSS
      уезжает сама строка `colors.border.error`, браузер выбрасывает объявление, и поле с
      ошибкой перестаёт быть красным: подпись под ним остаётся единственным признаком.
      Проверка `themeTokensResolve` держит это машиной.
    */
    error: {
      value: {
        _light: '{colors.red.500}',
        _dark: '{colors.red.400}',
      },
    },
    warning: {
      value: {
        _light: '{colors.orange.500}',
        _dark: '{colors.orange.400}',
      },
    },
    success: {
      value: {
        _light: '{colors.green.500}',
        _dark: '{colors.green.400}',
      },
    },
    info: {
      value: {
        _light: '{colors.blue.500}',
        _dark: '{colors.blue.400}',
      },
    },
  },
  gray: {
    contrast: {
      value: '{colors.black}',
    },
    fg: {
      value: '{colors.gray.200}',
    },
    subtle: {
      value: '{colors.gray.800}',
    },
    muted: {
      value: {
        _light: '{colors.gray.200}',
        _dark: '{colors.gray.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.gray.300}',
        _dark: '{colors.gray.700}',
      },
    },
    solid: {
      value: {
        _light: '{colors.gray.900}',
        _dark: '{colors.white}',
      },
    },
    // Кольцо фокуса одно на весь продукт: фиолетовое, как у основной палитры. Серое кольцо
    // на серых иконочных кнопках было единственным местом, где фокус выглядел иначе.
    focusRing: {
      value: '{colors.purple.200}',
    },
  },
  red: {
    contrast: {
      value: {
        _light: 'white',
        _dark: 'white',
      },
    },
    fg: {
      value: {
        _light: '{colors.red.700}',
        _dark: '{colors.red.300}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.red.100}',
        _dark: '{colors.red.900}',
      },
    },
    muted: {
      value: {
        _light: '{colors.red.200}',
        _dark: '{colors.red.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.red.300}',
        _dark: '{colors.red.700}',
      },
    },
    solid: {
      value: {
        _light: '{colors.red.500}',
        _dark: '{colors.red.500}',
      },
    },
    focusRing: {
      value: {
        _light: '{colors.red.500}',
        _dark: '{colors.red.500}',
      },
    },
  },
  orange: {
    contrast: {
      value: {
        _light: 'white',
        _dark: 'black',
      },
    },
    fg: {
      value: {
        _light: '{colors.orange.700}',
        _dark: '{colors.orange.300}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.orange.100}',
        _dark: '{colors.orange.900}',
      },
    },
    muted: {
      value: {
        _light: '{colors.orange.200}',
        _dark: '{colors.orange.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.orange.300}',
        _dark: '{colors.orange.700}',
      },
    },
    solid: {
      value: {
        _light: '{colors.orange.400}',
        _dark: '{colors.orange.400}',
      },
    },
    focusRing: {
      value: {
        _light: '{colors.orange.500}',
        _dark: '{colors.orange.500}',
      },
    },
  },
  green: {
    contrast: {
      value: {
        _light: 'white',
        _dark: 'white',
      },
    },
    fg: {
      value: {
        _light: '{colors.green.700}',
        _dark: '{colors.green.300}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.green.100}',
        _dark: '{colors.green.900}',
      },
    },
    muted: {
      value: {
        _light: '{colors.green.200}',
        _dark: '{colors.green.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.green.300}',
        _dark: '{colors.green.700}',
      },
    },
    solid: {
      value: {
        _light: '{colors.green.600}',
        _dark: '{colors.green.600}',
      },
    },
    focusRing: {
      value: {
        _light: '{colors.green.500}',
        _dark: '{colors.green.500}',
      },
    },
  },
  blue: {
    contrast: {
      value: {
        _light: 'white',
        _dark: 'white',
      },
    },
    fg: {
      value: {
        _light: '{colors.blue.700}',
        _dark: '{colors.blue.300}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.blue.100}',
        _dark: '{colors.blue.900}',
      },
    },
    muted: {
      value: {
        _light: '{colors.blue.200}',
        _dark: '{colors.blue.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.blue.300}',
        _dark: '{colors.blue.700}',
      },
    },
    solid: {
      value: {
        _light: '{colors.blue.600}',
        _dark: '{colors.blue.600}',
      },
    },
    focusRing: {
      value: {
        _light: '{colors.blue.500}',
        _dark: '{colors.blue.500}',
      },
    },
  },
  yellow: {
    contrast: {
      value: {
        _light: 'black',
        _dark: 'black',
      },
    },
    fg: {
      value: {
        _light: '{colors.yellow.800}',
        _dark: '{colors.yellow.300}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.yellow.100}',
        _dark: '{colors.yellow.900}',
      },
    },
    muted: {
      value: {
        _light: '{colors.yellow.200}',
        _dark: '{colors.yellow.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.yellow.300}',
        _dark: '{colors.yellow.700}',
      },
    },
    solid: {
      value: {
        _light: '{colors.yellow.300}',
        _dark: '{colors.yellow.300}',
      },
    },
    focusRing: {
      value: {
        _light: '{colors.yellow.500}',
        _dark: '{colors.yellow.500}',
      },
    },
  },
  teal: {
    contrast: {
      value: {
        _light: 'white',
        _dark: 'white',
      },
    },
    fg: {
      value: {
        _light: '{colors.teal.700}',
        _dark: '{colors.teal.300}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.teal.100}',
        _dark: '{colors.teal.900}',
      },
    },
    muted: {
      value: {
        _light: '{colors.teal.200}',
        _dark: '{colors.teal.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.teal.300}',
        _dark: '{colors.teal.700}',
      },
    },
    solid: {
      value: {
        _light: '{colors.teal.600}',
        _dark: '{colors.teal.600}',
      },
    },
    focusRing: {
      value: {
        _light: '{colors.teal.500}',
        _dark: '{colors.teal.500}',
      },
    },
  },
  purple: {
    contrast: {
      value: 'white',
    },
    fg: {
      value: '{colors.purple.200}',
    },
    subtle: {
      value: '{colors.brand.700}',
    },
    // Одна ступень над `subtle`, та же, что у карточки под курсором (`bg.raised`). Раньше здесь
    // стоял `purple.200` — тот же цвет, что у `fg`: рецепт красит надпись первым, а подложку
    // при наведении вторым, и текст тихой фиолетовой кнопки исчезал под курсором, 1:1.
    muted: {
      value: '{colors.bg.raised}',
    },
    emphasized: {
      value: '{colors.purple.700}',
    },
    // purple.200 against `contrast: white` is 4.23:1 — under AA, which is why solid
    // brand buttons were avoided and Bump hand-rolls bgColor="purple.500" instead.
    // purple.500 is the shade that workaround already proved: 7.68:1.
    solid: {
      value: '{colors.purple.500}',
    },
    // `purple.200`, а не `purple.500`: кольцо фокуса — нетекстовый элемент, норма 3:1.
    // purple.500 давал 2,42:1 на странице и 1,73:1 на подсветке, Tab по интерфейсу не читался.
    // purple.200 даёт 4,39:1 на странице и 3,15:1 на подсветке — проходит везде.
    focusRing: {
      value: '{colors.purple.200}',
    },
  },
  pink: {
    contrast: {
      value: {
        _light: 'white',
        _dark: 'white',
      },
    },
    fg: {
      value: {
        _light: '{colors.pink.700}',
        _dark: '{colors.pink.300}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.pink.100}',
        _dark: '{colors.pink.900}',
      },
    },
    muted: {
      value: {
        _light: '{colors.pink.200}',
        _dark: '{colors.pink.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.pink.300}',
        _dark: '{colors.pink.700}',
      },
    },
    solid: {
      value: {
        _light: '{colors.pink.600}',
        _dark: '{colors.pink.600}',
      },
    },
    focusRing: {
      value: {
        _light: '{colors.pink.500}',
        _dark: '{colors.pink.500}',
      },
    },
  },
  cyan: {
    contrast: {
      value: {
        _light: 'white',
        _dark: 'white',
      },
    },
    fg: {
      value: {
        _light: '{colors.cyan.700}',
        _dark: '{colors.cyan.300}',
      },
    },
    subtle: {
      value: {
        _light: '{colors.cyan.100}',
        _dark: '{colors.cyan.900}',
      },
    },
    muted: {
      value: {
        _light: '{colors.cyan.200}',
        _dark: '{colors.cyan.800}',
      },
    },
    emphasized: {
      value: {
        _light: '{colors.cyan.300}',
        _dark: '{colors.cyan.700}',
      },
    },
    solid: {
      value: {
        _light: '{colors.cyan.600}',
        _dark: '{colors.cyan.600}',
      },
    },
    focusRing: {
      value: {
        _light: '{colors.cyan.500}',
        _dark: '{colors.cyan.500}',
      },
    },
  },

  // ── Presence / liveness family ──
  // Один смысл «сейчас/в сети/играет» → один набор токенов. Используют: онлайн-точка
  // аватара, presence-пилюль «играет сейчас», будущие «в сессии»-чипы.
  // ── Цвета площадок ──
  // Фирменный цвет внешней платформы — единственный случай, когда цвет приходит не от нас.
  // Пока это Discord: он значит «разговор идёт там», и человек узнаёт его раньше, чем читает
  // подпись. Синий был вбит числом в пяти местах, и когда понадобилось поправить оттенок,
  // правок оказалось пять вместо одной; шестнадцатеричное число вдобавок не переживает смену
  // темы и не находится поиском по токену.
  //
  // Набор тот же, что у присутствия: заливка, текст на подложке, сама подложка. Steam и Riot
  // лягут сюда же, когда понадобятся.
  platform: {
    discord: {
      // Знак и акцент: точка, иконка, залитая кнопка «зайти в комнату».
      solid: {
        value: '#5865F2',
      },
      // Текст и иконка на приглушённой подложке.
      fg: {
        value: '#5865F2',
      },
      // Подложка чипа «Discord». 15% — та же плотность, что у статуса «идёт сейчас»:
      // площадка называет себя, но не спорит с содержимым строки.
      bg: {
        value: 'color-mix(in srgb, #5865F2 15%, transparent)',
      },
      // Тише подложки: панель голосовой комнаты занимает целый блок, и на 15% она читалась
      // синим полем, а не фоном под текстом.
      subtle: {
        value: 'color-mix(in srgb, #5865F2 8%, transparent)',
      },
      // Край панели: тот же цвет, но виден на любой поверхности.
      border: {
        value: 'color-mix(in srgb, #5865F2 20%, transparent)',
      },
    },
    /*
      Метки ссылок в переписке. Площадку узнают по оттенку, поэтому здесь фирменные цвета, а не
      роли интерфейса. Раньше обе метки одалживали ближайшую роль ради нужного цвета: YouTube
      брал `fg.error`, а Twitch - ступень нашей фиолетовой шкалы, которая к Twitch отношения не
      имеет. Роль при этом врала: красный в системе значит ошибку.
    */
    youtube: {
      fg: {
        value: '#FF0000',
      },
    },
    twitch: {
      fg: {
        value: '#9146FF',
      },
    },
  },

  /*
    Двухпиксельная линия под шапкой окон про Discord: фирменный градиент из фиолетового в
    красный. Красный конец - не ступень нашей шкалы: ближайший красный в палитре заметно ярче и
    ломает переход. Значение живёт здесь, потому что тема и есть место для значений; в разметке
    от него остаётся имя.
  */
  accent: {
    brand: {
      value: 'linear-gradient(90deg, var(--chakra-colors-purple-solid) 0%, #D34C46 100%)',
    },
  },

  /*
    Значки пола в карточке человека. Синий и розовый здесь не роли интерфейса и не ступени
    палитры: это два опознавательных цвета, которые в продукте больше нигде не встречаются.
    Раньше они стояли числом прямо в компоненте.
  */
  gender: {
    male: {
      value: 'rgb(88, 171, 248)',
    },
    female: {
      value: 'rgb(236, 93, 129)',
    },
  },

  status: {
    game: {
      // Приглушённый зелёный выбранного игрового знака. Акцент только на значке,
      // поэтому игровая активность не конкурирует с действиями и онлайн-точкой.
      fg: {
        value: '#88af94',
      },
    },
    live: {
      // Заливка (онлайн-точка, live-индикаторы)
      solid: {
        value: '{colors.green.400}',
      },
      // Текст/иконка на подложке (контрастнее)
      fg: {
        value: '{colors.green.300}',
      },
      // Приглушённая подложка пилюля
      bg: {
        value: 'color-mix(in srgb, var(--chakra-colors-green-400) 15%, transparent)',
      },
      // Текст на зелёной заливке: «Ответить» в звонке, включённый показ экрана. Белый на
      // зелёном 400 не читается, поэтому тёмный - тот же зелёный 950, что стоял там числом.
      contrast: {
        value: '{colors.green.950}',
      },
    },
    /*
      «Отошёл»: был на сайте недавно, но не сейчас. Жёлтый 400 уже был этим цветом в двух местах
      - точка на аватаре и подпись под ником, - только записанный ступенью палитры. По ступени не
      видно, что это присутствие, а не предупреждение: у предупреждения свой оранжевый.
    */
    away: {
      solid: {
        value: '{colors.yellow.400}',
      },
      fg: {
        value: '{colors.yellow.400}',
      },
    },
  },
});
